import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhishingIcon from '@mui/icons-material/Phishing';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNotifications } from '../NotificationContext';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

function getNotificationIcon(type) {
  switch (type) {
    case 'buddy_invite':
      return <PersonAddIcon />;
    case 'buddy_catch':
      return <PhishingIcon />;
    case 'nearby_catch':
      return <LocationOnIcon />;
    default:
      return <NotificationsIcon />;
  }
}

function resolveImageUrl(path) {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const normalized = path.startsWith('/') ? path.slice(1) : path;
  return `${API_URL}/${normalized}`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const {
    unreadCount,
    notifications,
    loading,
    refreshNotifications,
    markRead,
    markAllRead,
  } = useNotifications();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    refreshNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markRead(notification.id);
    }
    handleClose();
    // Navigate based on notification type
    if (notification.type === 'buddy_invite' && notification.related_user_id) {
      navigate(`/profile/${notification.related_user_id}`);
    } else if (
      (notification.type === 'buddy_catch' || notification.type === 'nearby_catch') &&
      notification.related_user_id
    ) {
      navigate(`/profile/${notification.related_user_id}`);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 360, overflowY: 'auto' }}>
            {notifications.map((n) => (
              <ListItem
                key={n.id}
                button
                onClick={() => handleNotificationClick(n)}
                sx={{
                  bgcolor: n.is_read ? 'transparent' : 'action.hover',
                  borderLeft: n.is_read ? 'none' : '3px solid',
                  borderColor: 'primary.main',
                }}
              >
                <ListItemAvatar>
                  {n.related_user_profile_picture ? (
                    <Avatar src={resolveImageUrl(n.related_user_profile_picture)} />
                  ) : (
                    <Avatar>{getNotificationIcon(n.type)}</Avatar>
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={n.title}
                  secondary={
                    <>
                      {n.body && (
                        <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                          {n.body}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(n.created_at)}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
